// src/utils/whatsappLink.js
//
// Abre uma conversa no WhatsApp com número e mensagem pré-preenchida, em
// iOS, Android e web.
//
// ─── COMO ABRE ───────────────────────────────────────────────────────────────
//
//   nativo, WhatsApp instalado → AppLauncher `whatsapp://send?phone=…&text=…`
//                                (abre o app direto, sem passar por navegador)
//   nativo, sem WhatsApp       → Browser do Capacitor com `https://wa.me/…`
//                                (SFSafariViewController / Custom Tab, FORA da
//                                WebView; o wa.me oferece instalar/abrir no web)
//   web                        → window.open(wa.me) em aba nova
//
// `wa.me` NÃO pode entrar em `server.allowNavigation` do capacitor.config —
// entraria dentro da WebView e a pessoa ficaria presa numa página sem voltar.
//
// ─── O QUE ESTE HELPER EXIGE DO PROJETO NATIVO ───────────────────────────────
//
//   iOS: `LSApplicationQueriesSchemes` com `whatsapp` no Info.plist — sem
//        isso `canOpenUrl` responde false sempre e cai no fallback.
//   Android 11+: bloco <queries> com com.whatsapp e com.whatsapp.w4b no
//        AndroidManifest (package visibility) — mesma consequência.
//
// ─── NÚMERO ──────────────────────────────────────────────────────────────────
//
// Só dígitos, com DDI (55…), sem "+", espaço ou traço. NUNCA hardcoded no
// componente: vem de variável de ambiente (CRA só injeta `REACT_APP_*`).
// Sem número configurado o helper devolve false e quem chama não renderiza o
// botão (fail-closed, como o do VITA em vitaWhatsappLink.js).
//
// USO: atendimento/suporte apenas. Não usar em fluxo de venda ou assinatura —
// direcionar compra para fora do app fere a 3.1.1 da App Store.

import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { AppLauncher } from "@capacitor/app-launcher";

/** Número do suporte humano, só dígitos, ou `null` se não configurado. */
export function getSuporteWhatsappNumero() {
  return normalizarNumero(process.env.REACT_APP_SUPORTE_WHATSAPP_NUMERO);
}

/** `"+55 (11) 9…"` → `"5511…"`; devolve null se não parecer um número com DDI. */
export function normalizarNumero(bruto) {
  const digitos = String(bruto || "").replace(/\D/g, "");
  return digitos.length >= 12 ? digitos : null; // 55 + DDD + 8/9 dígitos
}

export function buildWhatsappSchemeUrl(numero, mensagem = "") {
  return `whatsapp://send?phone=${numero}&text=${encodeURIComponent(mensagem)}`;
}

export function buildWhatsappWebUrl(numero, mensagem = "") {
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
}

/**
 * Abre a conversa.
 * @param {{ numero?: string, mensagem?: string }} opcoes
 * @returns {Promise<boolean>} false só quando não há número configurado.
 */
export async function abrirWhatsapp({ numero, mensagem = "" } = {}) {
  const alvo = normalizarNumero(numero) || getSuporteWhatsappNumero();
  if (!alvo) {
    console.warn("[whatsappLink] número ausente (REACT_APP_SUPORTE_WHATSAPP_NUMERO).");
    return false;
  }
  const web = buildWhatsappWebUrl(alvo, mensagem);

  if (Capacitor.isNativePlatform()) {
    try {
      const { value: temApp } = await AppLauncher.canOpenUrl({ url: "whatsapp://" });
      if (temApp) {
        await AppLauncher.openUrl({ url: buildWhatsappSchemeUrl(alvo, mensagem) });
        return true;
      }
    } catch (e) {
      // Plugin ausente/sem registro no nativo: segue para o navegador.
      console.warn("[whatsappLink] AppLauncher indisponível:", e?.message);
    }
    try {
      await Browser.open({ url: web });
      return true;
    } catch (e) {
      window.location.href = web;
      return true;
    }
  }

  try {
    window.open(web, "_blank", "noopener,noreferrer");
  } catch (e) {
    window.location.href = web;
  }
  return true;
}
